import React, { useEffect, useState } from 'react';

import { Toggle } from '@/components/shared/data-display/toggle-compare';
import { ToggleHeaderVariant } from '@/components/shared/data-display/toggle-header';
import { Text } from '@/components/shared/element/text';
import { Grid } from '@/components/shared/layout/grid';
import { removeEmptyTags } from '@/components/shared/utility/format';
import { useWindowSize } from '@/components/shared/utility/hooks';
import { styled } from '@/stitches.config';

export const StyledToggleContent = styled('div', {
  '& table': {
    '& th, & td': {
      fontSize: '13px',
      lineHeight: '150%',
    },
  },
  '& label': {
    display: 'block',
    color: '$primary',
    textDecoration: 'underline 1px',
    'text-underline-position': 'from-font',
    cursor: 'pointer',
    '& span': {
      display: 'block',
      color: 'inherit !important',
      fontSize: 'inherit !important',
    },
  },
  '& input[type=checkbox].hide': {
    display: 'none',
    '&:checked + table': {
      display: 'table',
    },
  },
  '& table.hide': {
    display: 'none',
  },
});

const StyledToggleContentDetail = styled('div', {
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontFamily: '$bold',
    fontSize: 'inherit',
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
  '& span': {
    lineHeight: '150%',
  },
  '& > *': {
    '& > *': {
      fontSize: 'inherit !important',
    },
  },
});

interface FormToggle {
  css?: any;
  className?: string;
  dataTrack?: string;
  dataTest?: string;
  title: string;
  detail: string;
  remark?: string;
  isShow?: boolean;
  variant?: ToggleHeaderVariant;
}

export const FormToggle: React.FC<FormToggle> = ({
  title,
  detail,
  remark,
  variant,
  isShow = false,
  ...props
}: FormToggle) => {
  const windowSize = useWindowSize();
  const [showRemark, setShowRemark] = useState<boolean>(false);

  useEffect(() => {
    if (!windowSize.width) return;
    setShowRemark(windowSize.width >= 1024);
  }, [windowSize.width]);

  const handleToggleRemark = () => {
    setShowRemark(!showRemark);
  };

  return (
    <Grid rowGap="6" {...props}>
      <Toggle
        title={title}
        onToggle={handleToggleRemark}
        isOpen={showRemark || isShow}
        isDisable={isShow}
        variant={variant}
      >
        <StyledToggleContent data-test="panel_condition_desc">
          <Grid rowGap="10">
            {!!detail && (
              <StyledToggleContentDetail>
                <Text
                  as="div"
                  size="text-body-2"
                  variant="text-gray-dark"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: removeEmptyTags(detail),
                  }}
                />
              </StyledToggleContentDetail>
            )}
            {!!remark && (
              <Grid rowGap="2">
                <Text
                  as="h3"
                  font="bold"
                  size="text-caption"
                  variant="text-gray-dark"
                >
                  หมายเหตุ
                </Text>
                <Text
                  as="div"
                  size="text-caption"
                  variant="text-gray-dark"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: removeEmptyTags(remark),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </StyledToggleContent>
      </Toggle>
    </Grid>
  );
};

FormToggle.displayName = 'FormToggle';
